import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Target, Upload, FileText, Download, Trash2, X, MessageSquare } from "lucide-react";
import { endpoints, API } from "@/lib/api";
import { toast } from "sonner";
import { SendToClientButton } from "@/components/common/SendToClientButton";
import PerformanceChart from "@/components/objectives/PerformanceChart";

const STATUS_TONE = {
  "Not Started": "bg-slate-100 text-slate-600 border-slate-200",
  "On Track": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "At Risk": "bg-amber-50 text-amber-700 border-amber-200",
  "Off Track": "bg-rose-50 text-rose-700 border-rose-200",
  "Met": "bg-indigo-50 text-indigo-700 border-indigo-200",
};

const empty = (year) => ({
  client_slug: "",
  year: year,
  objective: "",
  kpi: "",
  target_value: 0,
  unit: "%",
  direction: "up",
  frequency: "Monthly",
  responsible_person: "",
  status: "Not Started",
  notes: "",
});

export default function ObjectiveModal({ open, objectiveId, defaultClientSlug, defaultYear, clients = [], onOpenChange, onChanged }) {
  const [form, setForm] = useState(empty(defaultYear || new Date().getFullYear()));
  const [loaded, setLoaded] = useState(null);
  const [meta, setMeta] = useState({ frequencies: ["Monthly"], statuses: ["Not Started"], directions: ["up", "down"], periods_by_frequency: { Monthly: ["Jan"] } });
  const [tab, setTab] = useState("tracking");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [me, setMe] = useState({ name: "" });
  const fileRef = useRef(null);
  const isEdit = !!objectiveId;

  useEffect(() => {
    if (!open) return;
    endpoints.objectiveMetadata().then(setMeta).catch(() => {});
    endpoints.me().then(setMe).catch(() => {});
    if (isEdit) {
      endpoints.getObjective(objectiveId).then((o) => { setLoaded(o); setForm({ ...empty(o.year), ...o }); setTab("tracking"); });
    } else {
      setLoaded(null);
      setForm({ ...empty(defaultYear || new Date().getFullYear()), client_slug: defaultClientSlug || clients[0]?.slug || "" });
      setTab("details");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, objectiveId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const periods = meta.periods_by_frequency?.[form.frequency] || ["Jan"];
  const measurementByPeriod = useMemo(() => {
    const map = {};
    (loaded?.measurements || []).forEach((m) => { map[m.period] = m; });
    return map;
  }, [loaded]);

  const save = async (e) => {
    e?.preventDefault?.();
    if (!form.objective.trim()) return toast.error("Objective is required");
    if (!form.client_slug) return toast.error("Select a client");
    setSaving(true);
    try {
      if (isEdit) {
        const { client_slug: _cs, ...rest } = form;
        const upd = await endpoints.updateObjective(objectiveId, rest);
        setLoaded(upd);
        toast.success("Objective updated");
      } else {
        const c = await endpoints.createObjective(form);
        toast.success("Objective created");
        onChanged?.(c);
        return;
      }
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${loaded?.objective}"? This cannot be undone.`)) return;
    setSaving(true);
    try { await endpoints.deleteObjective(objectiveId); toast.success("Deleted"); onChanged?.(); }
    catch { toast.error("Could not delete"); } finally { setSaving(false); }
  };

  const savePeriod = async (period, value, note) => {
    if (!isEdit) { toast.error("Save the objective first"); return; }
    try {
      const upd = await endpoints.upsertMeasurement(objectiveId, {
        period,
        value: value === "" || value === null ? null : Number(value),
        note: note || "",
        recorded_by: me.name,
      });
      setLoaded(upd);
    } catch (err) {
      toast.error("Could not save measurement");
    }
  };

  const uploadFile = async (file) => {
    if (!isEdit) { toast.error("Save the objective first"); return; }
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File too large — max 5 MB"); return; }
    setUploading(true);
    try {
      await endpoints.uploadObjectiveEvidence(objectiveId, file, me.name);
      const upd = await endpoints.getObjective(objectiveId);
      setLoaded(upd);
      toast.success(`${file.name} uploaded`);
    } catch (err) { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const deleteEv = async (evId) => {
    if (!window.confirm("Remove this file?")) return;
    try {
      await endpoints.deleteObjectiveEvidence(objectiveId, evId);
      const upd = await endpoints.getObjective(objectiveId);
      setLoaded(upd);
    } catch { toast.error("Could not remove"); }
  };

  const addComment = async () => {
    if (!commentText.trim()) return;
    try {
      const upd = await endpoints.addObjectiveComment(objectiveId, { text: commentText.trim(), author: me.name });
      setLoaded(upd);
      setCommentText("");
    } catch { toast.error("Could not add comment"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="modal-objective" className="sm:max-w-[900px] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 text-white flex items-center justify-center shadow-md shadow-indigo-500/25">
              <Target className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="flex items-center gap-2 flex-wrap">
                {isEdit ? (
                  <>
                    <span className="truncate">{loaded?.objective || "…"}</span>
                    {loaded && <Badge variant="outline" className={`text-[10px] rounded-full ${STATUS_TONE[loaded.status] || ""}`}>{loaded.status}</Badge>}
                  </>
                ) : "New Strategic Objective"}
              </DialogTitle>
              <DialogDescription>
                {isEdit
                  ? `${loaded?.client_name || ""} · ${loaded?.year || ""} · Target ${loaded?.target_value ?? ""}${loaded?.unit ? " " + loaded.unit : ""} (${loaded?.direction === "up" ? "higher is better" : "lower is better"})`
                  : "Define an objective, KPI, target and cadence."}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="pt-2">
          <TabsList className="bg-slate-100 dark:bg-slate-800 mb-3 h-auto flex-wrap">
            <TabsTrigger value="details" data-testid="obj-tab-details" className="text-xs h-8">Details</TabsTrigger>
            {isEdit && <TabsTrigger value="tracking" data-testid="obj-tab-tracking" className="text-xs h-8">Tracking matrix</TabsTrigger>}
            {isEdit && <TabsTrigger value="chart" data-testid="obj-tab-chart" className="text-xs h-8">Chart</TabsTrigger>}
            {isEdit && <TabsTrigger value="evidence" data-testid="obj-tab-evidence" className="text-xs h-8">Evidence ({(loaded?.evidence || []).length})</TabsTrigger>}
            {isEdit && <TabsTrigger value="comments" data-testid="obj-tab-comments" className="text-xs h-8">Comments ({(loaded?.comments || []).length})</TabsTrigger>}
          </TabsList>

          {tab === "details" && (
            <form onSubmit={save} className="space-y-3">
              <div>
                <Label>Objective</Label>
                <Textarea data-testid="obj-objective-input" rows={2} value={form.objective} onChange={(e) => set("objective", e.target.value)} placeholder="e.g. Reduce customer complaints" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Client</Label>
                  <Select value={form.client_slug} onValueChange={(v) => set("client_slug", v)} disabled={isEdit}>
                    <SelectTrigger data-testid="obj-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                    <SelectContent>{clients.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.company_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Year</Label>
                  <Input data-testid="obj-year-input" type="number" value={form.year} onChange={(e) => set("year", Number(e.target.value))} />
                </div>
                <div className="col-span-2">
                  <Label>KPI</Label>
                  <Input data-testid="obj-kpi-input" value={form.kpi} onChange={(e) => set("kpi", e.target.value)} placeholder="e.g. Complaints per month" />
                </div>
                <div>
                  <Label>Target value</Label>
                  <Input data-testid="obj-target-input" type="number" step="any" value={form.target_value} onChange={(e) => set("target_value", Number(e.target.value))} />
                </div>
                <div>
                  <Label>Unit</Label>
                  <Input data-testid="obj-unit-input" value={form.unit} onChange={(e) => set("unit", e.target.value)} placeholder="%, count, days..." />
                </div>
                <div>
                  <Label>Direction</Label>
                  <Select value={form.direction} onValueChange={(v) => set("direction", v)}>
                    <SelectTrigger data-testid="obj-direction-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="up">Higher is better</SelectItem>
                      <SelectItem value="down">Lower is better</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Frequency</Label>
                  <Select value={form.frequency} onValueChange={(v) => set("frequency", v)}>
                    <SelectTrigger data-testid="obj-frequency-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{(meta.frequencies || []).map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Responsible person</Label>
                  <Input data-testid="obj-owner-input" value={form.responsible_person} onChange={(e) => set("responsible_person", e.target.value)} />
                </div>
                <div className="col-span-2">
                  <Label>Notes</Label>
                  <Textarea data-testid="obj-notes-input" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
                </div>
              </div>
              <DialogFooter className="flex-wrap gap-2">
                {isEdit && (
                  <Button data-testid="obj-delete-button" type="button" variant="ghost" className="text-rose-600 mr-auto" onClick={remove} disabled={saving}>
                    <Trash2 className="h-4 w-4 mr-1" /> Delete
                  </Button>
                )}
                <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
                <Button data-testid="obj-submit-button" type="submit" className="brand-gradient text-white" disabled={saving}>
                  {saving ? "Saving..." : isEdit ? "Save changes" : "Create objective"}
                </Button>
              </DialogFooter>
            </form>
          )}

          {tab === "tracking" && isEdit && loaded && (
            <div>
              <div className="rounded-2xl border border-slate-200 dark:border-white/10 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50/70 dark:bg-slate-900/60 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                    <tr>
                      <th className="text-left px-3 py-2">Period</th>
                      <th className="text-right px-3 py-2">Actual</th>
                      <th className="text-right px-3 py-2">Target</th>
                      <th className="text-left px-3 py-2 min-w-[220px]">Note</th>
                      <th className="text-center px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {periods.map((p) => {
                      const mm = measurementByPeriod[p];
                      const val = mm?.value;
                      const met = val != null && (loaded.direction === "up" ? val >= loaded.target_value : val <= loaded.target_value);
                      return (
                        <tr key={p} className="border-t border-slate-100 dark:border-white/5">
                          <td className="px-3 py-2 font-semibold text-xs">{p}</td>
                          <td className="px-3 py-2 text-right">
                            <Input
                              data-testid={`obj-period-${p}-value`}
                              type="number"
                              step="any"
                              defaultValue={val ?? ""}
                              onBlur={(e) => savePeriod(p, e.target.value, mm?.note)}
                              className="h-8 w-24 text-right font-mono ml-auto"
                              placeholder="—"
                            />
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-xs text-slate-500">
                            {loaded.target_value}{loaded.unit ? ` ${loaded.unit}` : ""}
                          </td>
                          <td className="px-3 py-2">
                            <Input
                              defaultValue={mm?.note || ""}
                              onBlur={(e) => val != null && savePeriod(p, val, e.target.value)}
                              className="h-8 text-xs"
                              placeholder="Optional note"
                            />
                          </td>
                          <td className="px-3 py-2 text-center">
                            {val == null ? <span className="text-[10px] text-slate-400">—</span> :
                              <Badge variant="outline" className={`text-[10px] rounded-full ${met ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`}>
                                {met ? "Met" : "Missed"}
                              </Badge>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-[11px] text-slate-500 mt-2">Values save automatically when you tab away.</p>
            </div>
          )}

          {tab === "chart" && isEdit && loaded && (
            <div className="rounded-2xl border border-slate-200 dark:border-white/10 p-3 bg-white/60 dark:bg-slate-900/40">
              <PerformanceChart
                periods={periods}
                measurements={loaded.measurements || []}
                target={loaded.target_value || 0}
                unit={loaded.unit}
                direction={loaded.direction}
              />
              <div className="text-[11px] text-slate-500 text-center mt-2">
                Green band shows the success zone. Dots turn green when a period meets the target, rose when it misses.
              </div>
            </div>
          )}

          {tab === "evidence" && isEdit && (
            <div>
              <div className="flex justify-end mb-3">
                <input ref={fileRef} data-testid="obj-evidence-input" type="file" hidden onChange={(e) => uploadFile(e.target.files?.[0])} />
                <Button data-testid="obj-evidence-upload-button" type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}>
                  <Upload className="h-3.5 w-3.5 mr-1" /> {uploading ? "Uploading..." : "Upload evidence"}
                </Button>
              </div>
              {(loaded?.evidence || []).length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 dark:border-white/10 p-6 text-center text-xs text-slate-500">
                  Attach charts, dashboards or exports (5 MB per file).
                </div>
              ) : (
                <div className="space-y-2">
                  {loaded.evidence.map((e) => (
                    <div key={e.id} className="flex items-center gap-3 rounded-xl border border-slate-200 dark:border-white/10 bg-white/60 dark:bg-slate-900/40 p-2.5">
                      <div className="h-8 w-8 rounded-lg bg-indigo-50 dark:bg-indigo-500/15 text-indigo-600 flex items-center justify-center"><FileText className="h-4 w-4" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold truncate">{e.filename}</div>
                        <div className="text-[10px] text-slate-500">{(e.size/1024).toFixed(1)} KB · {e.uploaded_by}</div>
                      </div>
                      <Button asChild size="icon" variant="ghost" className="h-7 w-7"><a href={`${API}/objectives/${objectiveId}/evidence/${e.id}`} download><Download className="h-3.5 w-3.5" /></a></Button>
                      <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-rose-500" onClick={() => deleteEv(e.id)}><X className="h-3.5 w-3.5" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "comments" && isEdit && (
            <div>
              <div className="space-y-2 mb-3">
                {(loaded?.comments || []).length === 0 && <p className="text-xs text-slate-500 text-center py-3">No comments yet.</p>}
                {(loaded?.comments || []).map((c) => (
                  <div key={c.id} className="rounded-xl border border-slate-200 dark:border-white/10 bg-white/60 dark:bg-slate-900/40 p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold">{c.author}</span>
                      <span className="text-[10px] text-slate-500">{new Date(c.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{c.text}</p>
                  </div>
                ))}
              </div>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label>Add comment</Label>
                  <Textarea data-testid="obj-comment-input" rows={2} value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder="Share context, links or next steps..." />
                </div>
                <Button data-testid="obj-comment-submit" type="button" className="brand-gradient text-white" onClick={addComment}>
                  <MessageSquare className="h-3.5 w-3.5 mr-1" /> Post
                </Button>
              </div>
            </div>
          )}
        </Tabs>

        {isEdit && (
          <DialogFooter className="pt-3 flex-wrap gap-2 border-t border-slate-100 dark:border-white/5 mt-2">
            <Button asChild variant="outline" size="sm" data-testid="obj-export-pdf-button">
              <a href={endpoints.exportObjectivePdfUrl(objectiveId)} download><Download className="h-3.5 w-3.5 mr-1" /> Export PDF</a>
            </Button>
            <SendToClientButton kind="objective" refId={objectiveId} />
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
